import { ToggleButton, CustomButton } from '@/shared/ui/components';
import { CloseIcon } from '@/shared/ui/assets';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useEcgViewerGuideline } from './useEcgViewerGuideline';
import ecgChartGuide1 from './assets/ecg-chart-guide-1.png';
import ecgChartGuide2 from './assets/ecg-chart-guide-2.png';
import ecgChartGuide3 from './assets/ecg-chart-guide-3.png';
import ecgViewerTabGuide from './assets/ecg-viewer-tab-guide.png';
import eventListGuide1 from './assets/event-list-guide-1.png';
import eventListGuide2 from './assets/event-list-guide-2.png';
import hrChartGuide1 from './assets/hr-chart-guide-1.png';
import hrChartGuide10 from './assets/hr-chart-guide-10.png';
import hrChartGuide11 from './assets/hr-chart-guide-11.png';
import hrChartGuide12 from './assets/hr-chart-guide-12.png';
import hrChartGuide13 from './assets/hr-chart-guide-13.png';
import hrChartGuide2 from './assets/hr-chart-guide-2.png';
import hrChartGuide3 from './assets/hr-chart-guide-3.png';
import hrChartGuide4 from './assets/hr-chart-guide-4.png';
import hrChartGuide5 from './assets/hr-chart-guide-5.png';
import hrChartGuide6 from './assets/hr-chart-guide-6.png';
import hrChartGuide7 from './assets/hr-chart-guide-7.png';
import hrChartGuide8 from './assets/hr-chart-guide-8.png';
import hrChartGuide9 from './assets/hr-chart-guide-9.png';
import CATEGORY from './constant';
import type { EcgViewerGuidelineProps } from './ecgViewerGuideline.types';
import './style.scss';

/**
 * Renders the ECG viewer tab's in-app guideline pane with its four illustrated help sections. Pulls state, the category list and the back, category and image-load handlers from useEcgViewerGuideline, composes the ECG-viewer-tab, HR-chart, ECG-chart and event-list markup where every screenshot reports onLoad and is backed by a Skeleton until loaded, and renders a close button plus one ToggleButton per category above the active section.
 *
 * @param handleClickBack - Callback invoked when the guideline pane is closed.
 * @returns The guideline pane markup.
 */
const EcgViewerGuideline = ({ handleClickBack }: EcgViewerGuidelineProps) => {
  const { state, categoryList, onClickBack, onClickCategory, onLoadImg } = useEcgViewerGuideline({
    handleClickBack,
  });

  const ecgViewerTabHTML = (
    <div className="content-container">
      <div className="header">{t`ECG viewer tab`}</div>
      <div className="mt2">
        <span>
          {t`The "ECG viewer" tab is a tab that allows clinic users to view raw ECG data at any specified timeframe from the downloaded data with the events included. There are 6 main components in the ECG viewer tab:`}
        </span>
        <ol className="list-guide-line" aria-label={t`ECG viewer tab components`}>
          <li className="list-item">{t`Date picker`}</li>
          <li className="list-item">
            {t`HR chart: displays the heart's rhythm and electrical activity of a specific date/day.`}
          </li>
          <li className="list-item">
            {t`Event indicators: a checkbox to show all the events that occurred within the downloaded ECG data of the selected day on the HR chart.`}
          </li>
          <li className="list-item">
            {t`Event information bar: displays the selected 5-minute ECG data, including date and time, and min-max HR.`}
          </li>
          <li className="list-item">
            {t`ECG viewer Screen: displays the selected 5-minute ECG data.`}
          </li>
          <li className="list-item">
            {t`Event List: a list of all the events that occurred within the downloaded ECG data of the selected day.`}
          </li>
          <li className="list-item">
            {t`Refresh button: reload and update the events of the selected day.`}
          </li>
        </ol>
        <img
          src={ecgViewerTabGuide}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(ecgViewerTabGuide, '1');
          }}
        />
        {state.loadedImgsPage1 !== ecgViewerTabGuide && <Skeleton height={345} width="100%" />}
      </div>
    </div>
  );

  const hrChartHTML = (
    <div className="content-container">
      <div className="header">{t`Read and manage HR chart`}</div>
      <div className="mt2">
        <span>{t`To view the HR chart of a specific date/day, you can`}</span>
        <ol className="list-guide-line" aria-label={t`HR chart navigation steps`}>
          <li className="list-item">
            {t`Use the Date picker to select the date from the drop-down selection.`}
          </li>
          <li className="list-item">
            {t`Use the "angle left" and "angle right" button to go to the next day or go back to the previous day.`}
          </li>
        </ol>
        <img
          src={hrChartGuide1}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(hrChartGuide1, '2');
          }}
        />
        {!_.includes(state.loadedImgsPage2, hrChartGuide1) && (
          <Skeleton height={128} width="100%" />
        )}
        <div className="mt-12">
          {t`When hovering over the chart, a blue vertical line indicating the time and the average heart rate of that time will be displayed.`}
        </div>
        <img
          src={hrChartGuide2}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(hrChartGuide2, '2');
          }}
        />
        {!_.includes(state.loadedImgsPage2, hrChartGuide2) && (
          <Skeleton height={128} width="100%" />
        )}
        <div className="mt-12">
          {t`When hovering over the period of time on the HR chart that does not have data, the label shall display the time and “NA” for heart rate at that time.`}
        </div>
        <img
          src={hrChartGuide3}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(hrChartGuide3, '2');
          }}
        />
        {!_.includes(state.loadedImgsPage2, hrChartGuide3) && (
          <Skeleton height={128} width="100%" />
        )}
        <div className="mt-12">
          {t`When clicking on the HR chart, the vertical line shall be fixed on the HR chart and the system shall select the timeframe of 2.5 minutes before and after the time of the vertical line to draw the 5-minute ECG data in the ECG viewer Screen below.`}
        </div>
        <img
          src={hrChartGuide4}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(hrChartGuide4, '2');
          }}
        />
        {!_.includes(state.loadedImgsPage2, hrChartGuide4) && (
          <Skeleton height={128} width="100%" />
        )}
        <div className="mt-12">
          {t`NOTE: You CANNOT click on the HR chart of the time periods that do not have any data. To view a zoomed-in version of a timeframe in the HR chart, you can:`}
        </div>
        <ul className="unorder-list-guide-line" aria-label={t`Zoom instructions`}>
          <li>
            {t`Click on the chart and drag, the selected area shall be highlighted and zoomed in.`}
          </li>
        </ul>
        <img
          src={hrChartGuide5}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(hrChartGuide5, '2');
          }}
        />
        {!_.includes(state.loadedImgsPage2, hrChartGuide5) && (
          <Skeleton height={128} width="100%" />
        )}
        <div className="mt-12">{`${t`NOTE`}:`}</div>
        <ul className="unorder-list-guide-line" aria-label={t`Zoom notes`}>
          <li>
            {t`Zooming in the HR chart will not change the data in the ECG chart and event information in the Event bar`}
          </li>
          <li>
            {t`In the case the user wants to zoom in a period  of time that is less than 1 hour, the HR chart will display the selected period of time in the middle of 1 hour.`}
          </li>
        </ul>
        <img
          src={hrChartGuide6}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(hrChartGuide6, '2');
          }}
        />
        {!_.includes(state.loadedImgsPage2, hrChartGuide6) && (
          <Skeleton height={128} width="100%" />
        )}
        <img
          src={hrChartGuide7}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(hrChartGuide7, '2');
          }}
        />
        {!_.includes(state.loadedImgsPage2, hrChartGuide7) && (
          <Skeleton height={128} width="100%" />
        )}
        <ul className="unorder-list-guide-line" aria-label={t`Large zoom note`}>
          <li className="mt-12">
            {t`In the case the user wants to zoom in a period of time that is more than or equal to 1 hour, the HR chart will display the entire selected period of time.`}
          </li>
        </ul>
        <img
          src={hrChartGuide8}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(hrChartGuide8, '2');
          }}
        />
        {!_.includes(state.loadedImgsPage2, hrChartGuide8) && (
          <Skeleton height={128} width="100%" />
        )}
        <img
          src={hrChartGuide9}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(hrChartGuide9, '2');
          }}
        />
        {!_.includes(state.loadedImgsPage2, hrChartGuide9) && (
          <Skeleton height={128} width="100%" />
        )}
        <div className="mt-12">
          {t`To zoom-out, click on the “Reset zoom” button to zoom-out to the original view of the HR chart.`}
        </div>
        <img
          src={hrChartGuide10}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(hrChartGuide10, '2');
          }}
        />
        {!_.includes(state.loadedImgsPage2, hrChartGuide10) && (
          <Skeleton height={128} width="100%" />
        )}
        <div className="mt-12">
          {t`To view when all the events that occurred within the downloaded ECG data of the specific date/day, select the “Show event indicators” checkbox. It shall be pre-selected as the default. Red lines will be displayed on the HR chart indicating the events occurred within the specific date/day.`}
        </div>
        <img
          src={hrChartGuide11}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(hrChartGuide11, '2');
          }}
        />
        {!_.includes(state.loadedImgsPage2, hrChartGuide11) && (
          <Skeleton height={128} width="100%" />
        )}
        <div className="mt-12">
          {t`By default, the Event information bar shall display the date & time, and the heart rate information (min-max HR) of the first 5-minute ECG data of the downloaded ECG data. The information inside the header shall change accordingly to where you click on the HR chart.`}
        </div>
        <img
          src={hrChartGuide12}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(hrChartGuide12, '2');
          }}
        />
        {!_.includes(state.loadedImgsPage2, hrChartGuide12) && (
          <Skeleton height={128} width="100%" />
        )}
        <div className="mt-12">
          {t`The Event information bar will also be changed when you click on the row of an event in the Event List below the ECG viewer Screen:`}
        </div>
        <ul className="unorder-list-guide-line" aria-label={t`Event row click behavior`}>
          <li>
            {t`The red line, an indicator of the occurred event in the HR chart, shall be highlighted in bold.`}
          </li>
          <li>{t`The color of the Event Information bar shall be changed to a red color.`}</li>
        </ul>
        <img
          src={hrChartGuide13}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(hrChartGuide13, '2');
          }}
        />
        {!_.includes(state.loadedImgsPage2, hrChartGuide13) && (
          <Skeleton height={128} width="100%" />
        )}
      </div>
    </div>
  );

  const ecgChartHTML = (
    <div className="content-container">
      <div className="header">{t`Read and manage ECG chart`}</div>
      <div className="mt2">
        <span>
          {t`By default, the ECG viewer Screen shall display the first 5-minute of ECG data from the downloaded ECG of the specific date/day. The ECG strip on the ECG viewer Screen shall change accordingly to where you click on the HR chart. There are different tools/filters you can use on the ECG chart:`}
        </span>
        <ol className="list-guide-line" aria-label={t`ECG chart tools`}>
          <li className="list-item">
            {t`Speed: There is wide range of speed you can select from 2.5 mm/s to 500mm/s. By default, the speed value is 25mm/s.`}
          </li>
          <li className="list-item">
            {t`Gain: There is wide range of gain you can select from 1mm/mV to 200mm/mV. By default, the gain value is 10mm/mV.`}
          </li>
          <li className="list-item">{t`High Pass Filter`}</li>
          <li className="list-item">{t`Low Pass Filter`}</li>
          <li className="list-item">{t`Show/Hide Channels`}</li>
          <li className="list-item">{t`Beat Marker Tool`}</li>
          <li className="list-item">{t`Zoom in the strip`}</li>
          <li className="list-item">{t`View all strip`}</li>
        </ol>
        <img
          src={ecgChartGuide1}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(ecgChartGuide1, '3');
          }}
        />
        {!_.includes(state.loadedImgsPage3, ecgChartGuide1) && (
          <Skeleton height={345} width="100%" />
        )}
        <div className="mt-12">
          {t`You can right-click on the scroll panel to change preview channel.`}
        </div>
        <img
          src={ecgChartGuide2}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(ecgChartGuide2, '3');
          }}
        />
        {!_.includes(state.loadedImgsPage3, ecgChartGuide2) && (
          <Skeleton height={345} width="100%" />
        )}
        <div className="mt-12">{t`You can right-click on the ECG viewer to view more tools.`}</div>
        <img
          src={ecgChartGuide3}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(ecgChartGuide3, '3');
          }}
        />
        {!_.includes(state.loadedImgsPage3, ecgChartGuide3) && (
          <Skeleton height={345} width="100%" />
        )}
      </div>
    </div>
  );

  const eventListHTML = (
    <div className="content-container">
      <div className="header">{t`Select and view an event in event list`}</div>
      <div className="mt2">
        <span>
          {t`The Event List below the ECG viewer Screen is a list of all the events that occurred within the downloaded ECG data of the selected day.`}
        </span>
        <div className="mt-12">{t`The list shall display the following information:`}</div>
        <ul className="unorder-list-guide-line" aria-label={t`Event list fields`}>
          <li>{t`Event ID`}</li>
          <li>{t`Trigger time`}</li>
          <li>{t`Event type`}</li>
          <li>{t`Symptoms`}</li>
          <li>{t`Technician comments`}</li>
        </ul>
        <div className="mt-12">
          {t`To filter the list of events by the event types, use the event-type filter in the top-right corner of the list.`}
        </div>
        <img
          src={eventListGuide1}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(eventListGuide1, '4');
          }}
        />
        {!_.includes(state.loadedImgsPage4, eventListGuide1) && (
          <Skeleton height={128} width="100%" />
        )}
        <div className="mt-12">
          {t`If the technician comments exceeds the allowed display on the row, you can hover over the technician comments and a tooltip shall display the full version of the comments.`}
        </div>
        <div className="mt-12">{t`When you click on the row of an event in the Event List:`}</div>
        <ul className="unorder-list-guide-line" aria-label={t`Event click behavior`}>
          <li>
            {t`The red line, an indicator of the occurred event in the HR chart, shall be highlighted in bold.`}
          </li>
          <li>
            {t`The information of the selected event in the Event list shall be displayed in the Event Information bar.`}
          </li>
          <li>{t`The color of the Event Information bar shall be changed to a red color.`}</li>
        </ul>
        <img
          src={eventListGuide2}
          className="mt-12"
          alt={t`Guide`}
          onLoad={() => {
            onLoadImg(eventListGuide2, '4');
          }}
        />
        {!_.includes(state.loadedImgsPage4, eventListGuide2) && (
          <Skeleton height={128} width="100%" />
        )}
      </div>
    </div>
  );

  /**
   * Picks the guideline section matching the currently selected help category. Switches on state.category and returns the ECG viewer tab, HR chart, ECG chart or event-list markup, and null for any other value.
   *
   * @returns The markup of the active guideline category, or null.
   */
  const renderGuidelineContent = () => {
    switch (state.category) {
      case CATEGORY.INTRODUCTION: {
        return ecgViewerTabHTML;
      }
      case CATEGORY.HR_CHART: {
        return hrChartHTML;
      }
      case CATEGORY.ECG_CHART: {
        return ecgChartHTML;
      }
      case CATEGORY.EVENT: {
        return eventListHTML;
      }
      default: {
        return null;
      }
    }
  };

  return (
    <div className="ecg-viewer-guideline">
      <div className="ecg-viewer-guideline__header-container">
        <CustomButton
          id="ecg-viewer-guideline-btn"
          style="neutral"
          type="secondary"
          onClick={onClickBack}
          icon={<ReactSVG src={CloseIcon} />}
          size="small"
          ariaLabel={t`Close`}
        />
        <strong className="header">{t`ECG viewer guideline`}</strong>
      </div>
      <div className="ecg-viewer-guideline__body-container">
        {_.map(categoryList.current, (item, index) => (
          <ToggleButton
            key={index}
            className={index > 0 ? 'ml-12' : ''}
            name="category"
            data={item}
            isActive={state.category === item.value}
            onClick={onClickCategory}
          />
        ))}
        {renderGuidelineContent()}
      </div>
    </div>
  );
};

export default EcgViewerGuideline;
